import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, Calculator } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { Section } from "@/components/common/Section";
import { DecimalInput } from "@/components/common/DecimalInput";
import { ErrorState } from "@/components/common/ErrorState";
import { LoadingList } from "@/components/common/LoadingList";
import { RoutePlanner } from "@/components/orders/RoutePlanner";
import { KalkylePanel } from "@/components/orders/KalkylePanel";
import { EstimateSummary } from "@/components/orders/EstimateSummary";
import { api, errorMessage, isConflict } from "@/lib/api";
import { fmtMoney, toInputDecimal } from "@/lib/format";
import { TEMPLATES } from "@/lib/i18n";
import { hasPlace } from "@/lib/maps";

const NONE = "__ingen__";
const EMPTY_LOGISTICS = { pickup: null, delivery: null, stops: [], distance_km: null, duration_min: null, distance_source: null, access_note: null, vehicle_resource_id: null, planned_time: null };
const EMPTY_KALKYLE = { items: [], transport_mode: "per_time", transport_rate: "", loads_override: null, loading_min: "", unloading_min: "", empty_km: "", ferry_count: null, ferry_unit_price: "", toll_amount: "", extras: [], discount_pct: "" };
const KALKYLE_KEYS = Object.keys(EMPTY_KALKYLE);

const emptyToNull = (v) => (v === "" || v === undefined ? null : v);

const DECIMAL_KEYS = ["transport_rate", "loading_min", "unloading_min", "empty_km", "ferry_unit_price", "toll_amount", "discount_pct"];

const kalkyleFromEstimate = (inputs) => {
  if (!inputs) return { ...EMPTY_KALKYLE };
  const out = { ...EMPTY_KALKYLE };
  KALKYLE_KEYS.forEach((k) => {
    if (inputs[k] === undefined || inputs[k] === null) return;
    out[k] = DECIMAL_KEYS.includes(k) ? toInputDecimal(inputs[k]) : inputs[k];
  });
  out.extras = (inputs.extras || []).map((e) => ({ description: e.description || "", amount: toInputDecimal(e.amount) }));
  let items = (inputs.items || []).map((it, i) => ({ key: `${it.catalog_item_id}-${i}`, catalog_item_id: it.catalog_item_id, quantity: toInputDecimal(it.quantity), unit: it.unit || null, unit_price: it.source === "manuell" ? toInputDecimal(it.unit_price_used ?? it.unit_price) : "", item_name: it.item_name }));
  // Eldre kalkylar med éi vare
  if (!items.length && inputs.item_id && inputs.quantity) items = [{ key: `${inputs.item_id}-0`, catalog_item_id: inputs.item_id, quantity: toInputDecimal(inputs.quantity), unit: inputs.unit || null, unit_price: "", item_name: inputs.item_name }];
  out.items = items;
  return out;
};

const fromOrder = (order) => ({
  template: order?.template || "anna",
  title: order?.title || "",
  description: order?.description || "",
  customer_id: order?.customer_id || "",
  planned_date: order?.planned_date || "",
  assigned_user_ids: order?.assigned_user_ids || [],
  responsible_user_id: order?.responsible_user_id || NONE,
  pricing_mode: order?.pricing_mode || "per_unit",
  fixed_price: order ? toInputDecimal(order.fixed_price) : "",
  default_hour_item_id: order?.default_hour_item_id || NONE,
  customer_reference: order?.customer_reference || "",
  internal_notes: order?.internal_notes || "",
  logistics: { ...EMPTY_LOGISTICS, ...(order?.logistics || {}), stops: order?.logistics?.stops || [] },
  kalkyle: kalkyleFromEstimate(order?.estimate?.inputs),
});

export default function OrderEditorPage() {
  const { orderId } = useParams();
  const editing = Boolean(orderId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(() => fromOrder(null));
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState({ estimate: null, loading: false, error: null });
  const previewRef = useRef(0);

  const orderQuery = useQuery({ queryKey: ["order", orderId], queryFn: async () => (await api.get(`/orders/${orderId}`)).data, enabled: editing, refetchOnWindowFocus: false, staleTime: Infinity });
  const customers = useQuery({ queryKey: ["customers"], queryFn: async () => (await api.get("/customers")).data });
  const users = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });
  const catalog = useQuery({ queryKey: ["catalog", "all"], queryFn: async () => (await api.get("/catalog")).data });
  const resources = useQuery({ queryKey: ["resources"], queryFn: async () => (await api.get("/resources")).data });
  const locations = useQuery({ queryKey: ["locations"], queryFn: async () => (await api.get("/locations")).data });
  const mapsStatus = useQuery({ queryKey: ["maps-status"], queryFn: async () => (await api.get("/maps/status")).data, staleTime: 5 * 60 * 1000 });
  const settings = useQuery({ queryKey: ["pricing-settings"], queryFn: async () => (await api.get("/pricing/settings")).data, staleTime: 5 * 60 * 1000 });

  useEffect(() => {
    if (editing && orderQuery.data) setForm(fromOrder(orderQuery.data));
  }, [editing, orderQuery.data]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setLogistics = (lg) => setForm((f) => ({ ...f, logistics: lg }));
  const setKalkyle = (k) => setForm((f) => ({ ...f, kalkyle: k }));
  const toggleUser = (id, checked) => {
    setForm((f) => {
      const ids = checked ? [...new Set([...f.assigned_user_ids, id])] : f.assigned_user_ids.filter((x) => x !== id);
      const responsible = ids.includes(f.responsible_user_id) ? f.responsible_user_id : ids[0] || NONE;
      return { ...f, assigned_user_ids: ids, responsible_user_id: responsible };
    });
  };

  const services = (catalog.data || []).filter((c) => c.category === "teneste");
  const vehicles = (resources.data || []).filter((r) => r.type === "koyretoy" && r.active !== false);
  const vehicle = vehicles.find((r) => r.id === form.logistics.vehicle_resource_id) || null;
  const customer = (customers.data || []).find((c) => c.id === form.customer_id) || null;

  // Kalkyle-inndata som blir sende til backend (same ved førehandsvising og lagring).
  const estimateInputs = useMemo(() => {
    const k = form.kalkyle;
    const lg = form.logistics;
    return {
      customer_id: form.customer_id || null,
      items: (k.items || []).filter((it) => it.catalog_item_id && emptyToNull(it.quantity) !== null).map((it) => ({ catalog_item_id: it.catalog_item_id, quantity: it.quantity, unit: it.unit || null, unit_price: emptyToNull(it.unit_price) })),
      vehicle_resource_id: lg.vehicle_resource_id || null,
      distance_km: emptyToNull(lg.distance_km),
      duration_min: emptyToNull(lg.duration_min),
      empty_km: emptyToNull(k.empty_km),
      loads_override: k.loads_override || null,
      loading_min: emptyToNull(k.loading_min),
      unloading_min: emptyToNull(k.unloading_min),
      transport_mode: k.transport_mode || "per_time",
      transport_rate: emptyToNull(k.transport_rate),
      ferry_count: k.ferry_count || null,
      ferry_unit_price: emptyToNull(k.ferry_unit_price),
      toll_amount: emptyToNull(k.toll_amount),
      extras: (k.extras || []).filter((e) => e && emptyToNull(e.amount) !== null).map((e) => ({ description: e.description || null, amount: e.amount })),
      discount_pct: emptyToNull(k.discount_pct),
      pricing_mode: form.pricing_mode,
      fixed_price: form.pricing_mode === "fixed" ? emptyToNull(form.fixed_price) : null,
    };
  }, [form.kalkyle, form.logistics, form.customer_id, form.pricing_mode, form.fixed_price]);

  const hasKalkyle = Boolean(
    estimateInputs.items.length ||
      (estimateInputs.vehicle_resource_id && estimateInputs.distance_km) ||
      estimateInputs.ferry_count ||
      estimateInputs.toll_amount ||
      estimateInputs.extras.length ||
      (estimateInputs.pricing_mode === "fixed" && estimateInputs.fixed_price),
  );

  // Debounced førehandsvising av kalkylen.
  const inputsKey = JSON.stringify(estimateInputs);
  useEffect(() => {
    if (!hasKalkyle) {
      setPreview({ estimate: null, loading: false, error: null });
      return undefined;
    }
    const id = ++previewRef.current;
    setPreview((p) => ({ ...p, loading: true, error: null }));
    const timer = setTimeout(async () => {
      try {
        const res = await api.post("/estimates/preview", JSON.parse(inputsKey));
        if (id === previewRef.current) setPreview({ estimate: res.data, loading: false, error: null });
      } catch (err) {
        if (id === previewRef.current) setPreview((p) => ({ estimate: p.estimate, loading: false, error: errorMessage(err, "Kunne ikkje rekne ut kalkylen.") }));
      }
    }, 450);
    return () => clearTimeout(timer);
  }, [inputsKey, hasKalkyle]);

  const mutation = useMutation({
    mutationFn: async () => {
      const lg = form.logistics;
      const logistics = {
        ...lg,
        pickup: hasPlace(lg.pickup) ? lg.pickup : null,
        delivery: hasPlace(lg.delivery) ? lg.delivery : null,
        stops: (lg.stops || []).filter(hasPlace),
        distance_km: emptyToNull(lg.distance_km),
        duration_min: emptyToNull(lg.duration_min),
      };
      const payload = {
        template: form.template,
        title: form.title,
        description: form.description || null,
        customer_id: form.customer_id,
        site_address: logistics.delivery?.address || logistics.delivery?.name || null,
        planned_date: form.planned_date || null,
        assigned_user_ids: form.assigned_user_ids,
        responsible_user_id: form.responsible_user_id === NONE ? null : form.responsible_user_id,
        pricing_mode: form.pricing_mode,
        fixed_price: form.pricing_mode === "fixed" ? form.fixed_price || null : null,
        default_hour_item_id: form.default_hour_item_id === NONE ? null : form.default_hour_item_id,
        customer_reference: form.customer_reference || null,
        internal_notes: form.internal_notes || null,
        logistics,
        estimate_inputs: hasKalkyle ? estimateInputs : null,
      };
      if (editing) return (await api.put(`/orders/${orderId}`, { ...payload, version: orderQuery.data.version })).data;
      return (await api.post("/orders", payload)).data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["order", data.id] });
      queryClient.invalidateQueries({ queryKey: ["counts"] });
      queryClient.invalidateQueries({ queryKey: ["history", data.id] });
      toast.success(editing ? "Ordra er oppdatert" : "Ordra er oppretta", { description: `${data.order_number} · ${data.title}` });
      navigate(`/ordrar/${data.id}`, { replace: true });
    },
    onError: (err) => {
      setError({ message: errorMessage(err), conflict: isConflict(err) });
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
  });

  const valid = form.title?.trim() && form.customer_id && (form.pricing_mode !== "fixed" || form.fixed_price);
  const assignedUsers = (users.data || []).filter((u) => form.assigned_user_ids?.includes(u.id));
  const backTo = editing ? `/ordrar/${orderId}` : "/ordrar";
  const submitLabel = mutation.isPending ? "Lagrar …" : editing ? "Lagre endringar" : "Opprett ordre";

  if (editing && orderQuery.isLoading) return <LoadingList rows={3} />;
  if (editing && orderQuery.isError) return <ErrorState message={errorMessage(orderQuery.error)} onRetry={() => orderQuery.refetch()} testId="order-editor-error" />;

  return (
    <form
      id="order-editor"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid && !mutation.isPending) mutation.mutate();
      }}
      className="space-y-5"
      data-testid="order-editor-page"
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0">
          <Button variant="ghost" size="sm" className="-ml-2 h-8 px-2 text-muted-foreground hover:text-foreground" asChild>
            <Link to={backTo} data-testid="order-editor-back"><ArrowLeft className="mr-1.5 h-4 w-4" /> {editing ? orderQuery.data?.order_number : "Ordrar"}</Link>
          </Button>
          <h1 className="mt-1 text-[22px] font-semibold leading-[1.15] md:text-[26px]" data-testid="order-editor-title">{editing ? `Endre ${orderQuery.data?.order_number}` : "Ny ordre"}</h1>
          <p className="mt-1 text-sm text-muted-foreground">Oppdrag, stader og rute, kalkyle og bemanning. Felt merkte med * er obligatoriske.</p>
        </div>
        <div className="hidden items-center gap-2 lg:flex">
          <Button type="button" variant="outline" className="h-10 bg-card" onClick={() => navigate(backTo)} data-testid="order-editor-cancel">Avbryt</Button>
          <Button type="submit" className="h-10 min-w-[150px]" disabled={!valid || mutation.isPending} data-testid="order-editor-submit">{submitLabel}</Button>
        </div>
      </div>

      {error ? (
        <Alert variant="destructive" className="bg-card" data-testid="order-editor-alert">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error.message}{error.conflict ? " Ordra vart endra av nokon andre – last inn sida på nytt og prøv igjen." : ""}</AlertDescription>
        </Alert>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
        <div className="min-w-0 space-y-5">
          <Section title="Oppdrag" testId="order-editor-oppdrag">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Kunde *</Label>
                <Select value={form.customer_id} onValueChange={(v) => set("customer_id", v)}>
                  <SelectTrigger className="md:h-10" data-testid="order-form-customer"><SelectValue placeholder="Vel kunde" /></SelectTrigger>
                  <SelectContent className="bg-popover">{(customers.data || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Ordremal *</Label>
                <Select value={form.template} onValueChange={(v) => set("template", v)}>
                  <SelectTrigger className="md:h-10" data-testid="order-form-template"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover">{TEMPLATES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4 space-y-1.5">
              <Label htmlFor="order-title">Tittel *</Label>
              <Input id="order-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="t.d. Levering av pukk til hyttefelt Nes" className="h-11 bg-card md:h-10" data-testid="order-form-title" required />
            </div>
            <div className="mt-4 space-y-1.5">
              <Label htmlFor="order-description">Skildring til mannskapet</Label>
              <Textarea id="order-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} placeholder="Kva skal gjerast, kva skal leverast, kven skal kontaktast." className="bg-card" data-testid="order-form-description" />
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="order-date">Planlagt dato</Label>
                <Input id="order-date" type="date" value={form.planned_date} onChange={(e) => set("planned_date", e.target.value)} className="tabular h-11 bg-card md:h-10" data-testid="order-form-date" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="order-ref">Kundereferanse / prosjekt</Label>
                <Input id="order-ref" value={form.customer_reference} onChange={(e) => set("customer_reference", e.target.value)} className="h-11 bg-card md:h-10" data-testid="order-form-reference" />
              </div>
            </div>
          </Section>

          <Section title="Stader og rute" description="Søk adresse eller vel lagra stad. Ruta blir rekna for personbil – kontroller tungbiltilkomst." testId="order-editor-rute">
            <RoutePlanner value={form.logistics} onChange={setLogistics} locations={locations.data || []} vehicles={vehicles} mapsAvailable={mapsStatus.data?.available !== false} />
          </Section>

          <Section
            title="Kalkyle"
            description="Prisoverslag for tilbod og kontroll. Blir lagra som låst snapshot på ordra."
            actions={<Calculator className="h-4 w-4 text-muted-foreground" aria-hidden="true" />}
            testId="order-editor-kalkyle"
          >
            <KalkylePanel inputs={form.kalkyle} onChange={setKalkyle} catalog={catalog.data || []} vehicle={vehicle} logistics={form.logistics} settings={settings.data || null} estimate={preview.estimate} customer={customer} />
            <div className="mt-6 border-t border-border pt-4 lg:hidden">
              <EstimateSummary estimate={preview.estimate} loading={preview.loading} error={preview.error} compact testId="estimate-inline" />
            </div>
          </Section>

          <Section title="Bemanning" testId="order-editor-bemanning">
            <div className="space-y-1.5">
              <Label>Tildelte medarbeidarar</Label>
              <div className="grid gap-1 rounded-lg border border-border bg-background p-2 sm:grid-cols-2" data-testid="order-form-assignees">
                {(users.data || []).map((u) => (
                  <label key={u.id} className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-md px-2 hover:bg-muted">
                    <Checkbox checked={form.assigned_user_ids?.includes(u.id)} onCheckedChange={(c) => toggleUser(u.id, Boolean(c))} data-testid={`order-form-assignee-${u.id}`} />
                    <span className="min-w-0 text-sm">
                      <span className="font-medium">{u.name}</span>
                      {u.title ? <span className="block truncate text-xs text-muted-foreground">{u.title}</span> : null}
                    </span>
                  </label>
                ))}
                {users.isSuccess && users.data.length === 0 ? <p className="p-2 text-sm text-muted-foreground">Ingen aktive brukarar.</p> : null}
              </div>
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Ansvarleg medarbeidar</Label>
                <Select value={form.responsible_user_id} onValueChange={(v) => set("responsible_user_id", v)}>
                  <SelectTrigger className="md:h-10" data-testid="order-form-responsible"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    <SelectItem value={NONE}>Ingen (første tildelte)</SelectItem>
                    {assignedUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Standard timeteneste</Label>
                <Select value={form.default_hour_item_id} onValueChange={(v) => set("default_hour_item_id", v)}>
                  <SelectTrigger className="md:h-10" data-testid="order-form-hour-item"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    <SelectItem value={NONE}>Ikkje vald</SelectItem>
                    {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Section>

          <Section title="Prising og interne notat" testId="order-editor-prising">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Prising</Label>
                <Select value={form.pricing_mode} onValueChange={(v) => set("pricing_mode", v)}>
                  <SelectTrigger className="md:h-10" data-testid="order-form-pricing-mode"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    <SelectItem value="per_unit">Per eining (timar, varer, ressursar)</SelectItem>
                    <SelectItem value="fixed">Fastpris</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.pricing_mode === "fixed" ? (
                <div className="space-y-1.5">
                  <Label htmlFor="order-fixed">Fastpris eks. mva (kr) *</Label>
                  <DecimalInput id="order-fixed" value={form.fixed_price} onChange={(v) => set("fixed_price", v)} placeholder="t.d. 5000" className="bg-card md:h-10" data-testid="order-form-fixed-price" />
                </div>
              ) : null}
            </div>
            <div className="mt-4 space-y-1.5">
              <Label htmlFor="order-internal">Interne notat (berre administrasjon)</Label>
              <Textarea id="order-internal" value={form.internal_notes} onChange={(e) => set("internal_notes", e.target.value)} rows={2} className="bg-card" data-testid="order-form-internal-notes" />
            </div>
          </Section>
        </div>

        <aside className="hidden lg:sticky lg:top-6 lg:block" data-testid="order-editor-aside">
          <Section title="Prisoverslag" description="Oppdaterer seg medan du fyller ut." testId="order-editor-estimate">
            <EstimateSummary estimate={preview.estimate} loading={preview.loading} error={preview.error} testId="estimate-aside" />
            <div className="mt-5 flex flex-col gap-2 border-t border-border pt-4">
              <Button type="submit" className="h-10" disabled={!valid || mutation.isPending} data-testid="order-editor-submit-aside">{submitLabel}</Button>
              {!valid ? <p className="text-xs text-muted-foreground">Fyll ut kunde og tittel{form.pricing_mode === "fixed" ? " og fastpris" : ""} for å lagre.</p> : null}
            </div>
          </Section>
        </aside>
      </div>

      {/* Mobil/nettbrett: fast handlingslinje nedst */}
      <div className="h-20 lg:hidden" aria-hidden="true" />
      <div className="safe-bottom fixed inset-x-0 bottom-16 z-20 border-t border-border bg-card md:bottom-0 lg:hidden" data-testid="order-editor-mobile-bar">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-2.5 md:px-6">
          <div className="min-w-0">
            <p className="text-[11px] text-muted-foreground">{preview.estimate ? "Overslag eks. mva" : "Ingen kalkyle"}</p>
            <p className="num truncate text-base font-semibold leading-tight" data-testid="order-editor-mobile-total">{preview.estimate ? fmtMoney(preview.estimate.subtotal_excl_vat) : "–"}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button type="button" variant="outline" className="h-11 bg-card md:h-10" onClick={() => navigate(backTo)} data-testid="order-editor-cancel-mobile">Avbryt</Button>
            <Button type="submit" className="h-11 md:h-10" disabled={!valid || mutation.isPending} data-testid="order-editor-submit-mobile">{submitLabel}</Button>
          </div>
        </div>
      </div>
    </form>
  );
}
